import React from 'react';
import { useLocation } from 'react-router-dom';
import Button from '@ajat/design-system/Button';
import './ErrorPage.css';

/* ══════════════════════════════════════════════════════════════════════════
 * BrandErrorBoundary — wraps <Routes> once in App.js.
 * On a render crash it resolves the brand from the pathname and renders that
 * brand's ServerError page; non-brand paths get a minimal neutral fallback.
 * Navigating to another route resets the boundary.
 * ══════════════════════════════════════════════════════════════════════════ */

class Boundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error('BrandErrorBoundary caught:', error, info);
  }

  componentDidUpdate(prevProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.renderFallback(() => this.setState({ hasError: false }));
    }
    return this.props.children;
  }
}

const NeutralFallback = ({ onRetry }) => (
  <div className="error-page error-page--neutral">
    <main className="error-page__main">
      <div className="error-page__code typo-h1-editorial" aria-hidden="true">500</div>
      <h1 className="error-page__title typo-h2">Something went wrong</h1>
      <p className="error-page__text typo-body">
        An unexpected error occurred. Please try again.
      </p>
      <div className="error-page__ctas">
        <Button size="secondary" variant="main" onClick={onRetry}>Try again</Button>
      </div>
    </main>
  </div>
);

const BrandErrorBoundary = ({ children, fallbacks = {} }) => {
  const location = useLocation();

  const renderFallback = (reset) => {
    const slug = Object.keys(fallbacks).find(
      (s) =>
        location.pathname === `/clothing/${s}` ||
        location.pathname.startsWith(`/clothing/${s}/`)
    );
    if (slug) {
      const Fallback = fallbacks[slug];
      return <Fallback onRetry={reset} />;
    }
    return <NeutralFallback onRetry={reset} />;
  };

  return (
    <Boundary resetKey={location.pathname} renderFallback={renderFallback}>
      {children}
    </Boundary>
  );
};

export default BrandErrorBoundary;
